/* !
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Component,
	Input,
	OnInit,
	ViewChild
} from '@angular/core';
import {
	UntypedFormControl
} from '@angular/forms';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	DynamicWizardComponent
} from '@dynamicComponents/dynamic-wizard/dynamic-wizard.component';
import {
	PaymentAllocationsComponent
} from '@insurance/components/payment-allocations/payment-allocations.component';
import {
	InsuranceConstants
} from '@insurance/constants/insurance-constants';
import {
	InsuranceService
} from '@insurance/services/insurance.service';
import {
	FormlyFieldConfig
} from '@ngx-formly/core';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	StringHelper
} from '@shared/helpers/string.helper';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	IWizardContext
} from '@shared/interfaces/dynamic-interfaces/wizard-context.interface';
import {
	IEntityInstance
} from '@shared/interfaces/entities/entity-instance.interface';
import {
	SiteLayoutService
} from '@shared/services/site-layout.service';

/* eslint-enable max-len */

@Component({
	selector: 'refund-details',
	templateUrl: './refund-details.component.html'
})

/**
 * A component representing a wizard step to collect
 * the refund details.
 *
 * @export
 * @class RefundDetailsComponent
 * @implements {OnInit}
 * @implements {IDynamicComponent<DynamicWizardComponent, IWizardContext>}
 */
export class RefundDetailsComponent implements OnInit,
		IDynamicComponent<DynamicWizardComponent, IWizardContext> {

	/**
	 * Initializes an instance of the refund details component.
	 *
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The entity instance api service used in this component.
	 * @param {SiteLayoutService} siteLayoutService
	 * The site layout service used in this component.
	 * @param {InsuranceService} insuranceService
	 * The insurance service used in this component.
	 * @memberof RefundDetailsComponent
	 */
	public constructor(
		public entityInstanceApiService: EntityInstanceApiService,
		public siteLayoutService: SiteLayoutService,
		public insuranceService: InsuranceService)
	{
	}

	/**
	 * Gets or sets the context of this dynamic component that will be set
	 * during initialization. The source is the content component and
	 * the data will be associated data that we desire to pass explicitly.
	 *
	 * @type {IDynamicComponentContext<
	 * 	DynamicWizardComponent,
	 * 	IWizardContext>}
	 * @memberof RefundDetailsComponent
	 */
	@Input() public context: IDynamicComponentContext<
		DynamicWizardComponent,
		IWizardContext>;

	/**
	 * Gets or sets the payment allocations child component reference.
	 *
	 * @type {PaymentAllocationsComponent}
	 * @memberof RefundDetailsComponent
	 */
	@ViewChild('paymentAllocations')
	public paymentAllocations: PaymentAllocationsComponent;

	/**
	 * Gets or sets the payment allocation settings retrieved from
	 * the product associated to the policy term.
	 *
	 * @type {any}
	 * @memberof RefundDetailsComponent
	 */
	public paymentAllocationSettings: any;

	/**
	 * Gets or sets the policy term entity instance that
	 * is associated with the refund.
	 *
	 * @type {IEntityInstance}
	 * @memberof RefundDetailsComponent
	 */
	public policyTermEntityInstance: IEntityInstance;

	/**
	 * Gets or sets the transaction associated with the payment.
	 *
	 * @type {IEntityInstance}
	 * @memberof RefundDetailsComponent
	 */
	public transaction: IEntityInstance;

	/**
	 * Gets or sets the formly layout used in implementing components.
	 *
	 * @type {FormlyFieldConfig[]}
	 * @memberof RefundDetailsComponent
	 */
	public dynamicFormlyLayout: FormlyFieldConfig[];

	/**
	 * Gets or sets the maximum allowable refund amount
	 * based on the current Ledger balance.
	 *
	 * @type {number}
	 * @memberof RefundDetailsComponent
	 */
	public refundLimit: number;

	/**
	 * Gets the context active menu item current data collected on the
	 * dynamic wizard steps.
	 *
	 * @type {any}
	 * @memberof RefundDetailsComponent
	 */
	public get currentData(): any
	{
		return this.context.source.activeMenuItem.currentData.data;
	}

	/**
	 * Gets a value indicating whether payment allocations are enabled.
	 *
	 * @type {boolean}
	 * @memberof RefundDetailsComponent
	 */
	public get paymentAllocationsEnabled(): boolean
	{
		return this.paymentAllocationSettings?.enabled ?? false;
	}

	/**
	 * Gets the amount field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof RefundDetailsComponent
	 */
	public get amountFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout
			.find(field => field.key === 'amount');
	}

	/**
	 * Gets the transaction number field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * The transaction number field configuration.
	 * @memberof RefundDetailsComponent
	 */
	public get transactionFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout
			.find(field => field.key === 'transactionId');
	}

	/**
	 * Gets the transaction amount field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof RefundDetailsComponent
	 */
	public get transactionAmountFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout
			.find(field => field.key === 'transactionAmount');
	}

	/**
	 * Gets the allocation amount field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof RefundDetailsComponent
	 */
	public get allocationAmountFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout
			.find(field => field.key === 'allocationAmount');
	}

	/**
	 * Implements the on initialization interface.
	 *
	 * @memberof RefundDetailsComponent
	 */
	public ngOnInit(): void
	{
		this.initializeComponent();
	}

	/**
	 * Loads data and initializes the controls
	 * required for this component to work.
	 *
	 * @async
	 * @memberof RefundDetailsComponent
	 */
	public async initializeComponent(): Promise<void>
	{
		this.refundLimit =
			this.currentData.amount || 0;

		this.policyTermEntityInstance =
			await this.insuranceService.getPolicyTerm(
				this.currentData.id);

		this.paymentAllocationSettings =
			await this.insuranceService
				.getProductPaymentAllocationSettings(
					this.policyTermEntityInstance.data.productName);

		this.initializeLayout();

		this.context.source.addToNext(
			this.next.bind(this));

		this.context.source.wizardStepLoading = false;
	}

	/**
	 * Moves to next step in the wizard.
	 *
	 * @memberof RefundDetailsComponent
	 */
	public next(): void
	{
		this.context.source.addOrUpdateStepData(
			{
				paymentAllocationsEnabled:
					this.paymentAllocationsEnabled,
				transactionNumber:
					this.transaction
						?.data
						.transactionNumber || AppConstants.empty
			});

		this.context.source.stepTo('Confirm');
	}

	/**
	 * Handles the transaction number field change event.
	 *
	 * @async
	 * @param {FormlyFieldConfig} field
	 * The formly field configuration.
	 * @returns {Promise<void>}
	 * A promise that resolves when the transaction change event is complete.
	 * @memberof RefundDetailsComponent
	 */
	public async onTransactionChange(
		field: FormlyFieldConfig): Promise<void>
	{
		this.transaction =
			AnyHelper.isNullOrWhitespace(
				field.formControl.value)
				? null
				: await this.insuranceService.getPolicyTermTransaction(
					field.formControl.value,
					InsuranceConstants.policyTermTransactionPrefix
						+ `${this.policyTermEntityInstance.data.productName}`);

		this.transactionFieldConfig
			.formControl
			?.updateValueAndValidity();
	}

	/**
	 * Loads the transactions for the transaction field.
	 *
	 * @return {Promise<IEntityInstance[]>}
	 * The promise containing the list of transaction options.
	 * @memberof RefundDetailsComponent
	 */
	public loadTransactions(): Promise<IEntityInstance[]>
	{
		return this.insuranceService
			.getPolicyTermTransactionsByPolicyTermId(
				this.currentData.id);
	}

	/**
	 * Handles the validity changed event for the dynamic formly component.
	 * This will update the validity of the wizard step for action buttons.
	 *
	 * @param {boolean} isValid
	 * The validity of the current displayed step data set.
	 * @memberof RefundDetailsComponent
	 */
	public validityChanged(
		isValid: boolean): void
	{
		this.context.source.validStepChanged(
			isValid
				&& (!this.paymentAllocationsEnabled
					|| this.paymentAllocations?.valid));
	}

	/**
	 * Handles the validity changed event for the payment allocations component.
	 * This will update the validity of the wizard step for action buttons.
	 *
	 * @param {boolean} isValid
	 * The validity of the payment allocations component.
	 * @memberof RefundDetailsComponent
	 */
	public paymentAllocationsValidityChanged(
		isValid: boolean): void
	{
		this.context.source.validStepChanged(
			isValid
				&& this.dynamicFormlyLayout[0]
					.formControl
					.parent
					.valid);
	}

	/**
	 * Creates the dynamic formly layout.
	 *
	 * @memberof RefundDetailsComponent
	 */
	private initializeLayout(): void
	{
		const allocationFieldsExpressions = {
			hide: () =>
				!this.paymentAllocationsEnabled
		};

		this.dynamicFormlyLayout =
			<FormlyFieldConfig[]>
			[
				{
					key: 'paymentType',
					type: FormlyConstants.customControls.customSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Refund Method',
						required: true,
						disabled: true,
						options: [
							{
								label: 'Cash',
								value: 'Cash'
							},
							{
								label: 'Check',
								value: 'Check'
							},
							{
								label: 'Credit Card',
								value: 'CreditCard'
							}
						],
						default: 'Check'
					}
				},
				{
					key: 'amount',
					type: FormlyConstants.customControls.customInputNumber,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Refund Amount',
						required: true,
						useCurrency: true
					},
					validators: {
						amountMustBeGreaterThanZero: {
							expression: (control: UntypedFormControl) =>
								!AnyHelper.isNull(control.value)
									&& control.value > 0,
							message: 'Refund amount must be greater than zero'
						},
						amountMustNotExceedLimit: {
							expression: (control: UntypedFormControl) =>
								!AnyHelper.isNull(control.value)
									&& control.value <= this.refundLimit,
							message:
								'Amount exceeds maximum allowable refund of '
									+ StringHelper.format(
										this.refundLimit.toString(),
										AppConstants.formatTypes.currency)
						}
					}
				},
				{
					key: 'transactionId',
					type: FormlyConstants.customControls.customDataSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Transaction',
						placeholder: 'Select an Option',
						required: true,
						showClear: true,
						dataPromise:
							this.loadTransactions.bind(this),
						labelTemplate: '${item.data.transactionNumber}',
						valueTemplate: '${item.id}',
						change: this.onTransactionChange.bind(this)
					},
					expressions: allocationFieldsExpressions,
					validators: {
						transactionAmountMustBeGreaterThanZero: {
							expression: () =>
								AnyHelper.isNull(
									this.transaction)
									|| AnyHelper.isNull(
										this.transaction
											.data
											.accounting
											?.directWrittenTotal)
									|| this.transaction
										.data
										.accounting
										.directWrittenTotal > 0,
							message:
								'Refund cannot be applied to a transaction '
									+ 'with a negative Transaction Amount.'
						}
					}
				},
				{
					key: 'transactionAmount',
					type: FormlyConstants.customControls.customInputNumber,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Transaction Amount',
						useCurrency: true,
						disabled: true
					},
					expressions: allocationFieldsExpressions
				},
				{
					wrappers: [
						FormlyConstants.customControls.customEmptyWrapper
					],
					props: {
						gridColumns: 6
					},
					expressions: allocationFieldsExpressions
				},
				{
					key: 'allocationAmount',
					type: FormlyConstants.customControls.customInputNumber,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Allocation Amount',
						useCurrency: true,
						disabled: true
					},
					expressions: allocationFieldsExpressions
				}
			];
	}
}